import { createStore } from "../lib/createStore";
import { http } from "../utils/config";
import { cacheClear } from "../utils/apiCache";
import { tokens } from "./tokens";

/**
 * State dùng chung thật của app: người đang đăng nhập + menu điều hướng.
 *
 * Thay hai slice `redux/mcoupon/user.js` và `redux/menu/getMenuOfUser.js`. Đây là
 * hai thứ duy nhất trong 46 slice cũ mà nhiều màn cùng đọc (user: 42 chỗ, menu: 5);
 * phần còn lại là dữ liệu của từng màn nên đã chuyển sang `useAsync`.
 *
 * Khác biệt so với redux-persist: state đọc từ localStorage **đồng bộ lúc nạp
 * module**, nên lần render đầu đã có dữ liệu. Trước đây `persistStore` rehydrate bất
 * đồng bộ mà app lại không bọc `PersistGate`, nên màn đầu luôn thấy `user = null`
 * rồi mới nhảy — chính lý do các màn phải tự dựng cờ `hasFetchedData`.
 */

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 phút, giữ đúng hành vi cũ

/* -------------------------------- User store ------------------------------- */

export const sessionStore = createStore(
  {
    /** Envelope nguyên bản từ `/auth/api/v1/getUser`: `{ status, data: {...} }`. */
    user: null,
    loadUser: false,
    /** Danh sách shop (chỉ ADMIN) — dùng ở EditStore / FormAddStore / FormAddTrademark. */
    allUser: null,
    isLoadAllUser: false,
    /** Danh sách shop có phân trang — màn Quản lý tài khoản. */
    userShop: null,
    isLoading: false,
    lastFetchTime: null,
  },
  {
    persistKey: "cms.session",
    // Chỉ lưu hồ sơ người dùng. `allUser`/`userShop` là danh sách có thể rất dài và
    // đổi liên tục — ghi xuống đĩa mỗi lần fetch vừa vô ích vừa dễ vượt quota.
    pick: (s) => ({ user: s.user, lastFetchTime: s.lastFetchTime }),
  }
);

/** Hook đọc trong component. Thay cho selector đọc slice `user` của Redux. */
export const useSession = sessionStore.use;

/**
 * Nạp hồ sơ + đồng bộ lại phân quyền.
 *
 * Mặc định tôn trọng cache 5 phút. Truyền `force` sau khi tự sửa hồ sơ của mình
 * (EditUser) để thấy ngay thay đổi.
 */
export async function loadUser({ force = false } = {}) {
  const { user, lastFetchTime } = sessionStore.get();
  if (!force && user && lastFetchTime && Date.now() - lastFetchTime < CACHE_TTL_MS) {
    return user;
  }

  sessionStore.set({ loadUser: true });
  try {
    const { data } = await http.get("/auth/api/v1/getUser");
    sessionStore.set({ user: data, loadUser: false, lastFetchTime: Date.now() });

    // Quyền có thể đã bị quản trị đổi từ lần đăng nhập trước — token cũ không biết.
    const permissions = data?.data?.permissions;
    if (permissions) {
      try {
        localStorage.setItem("permissions", permissions);
      } catch {
        /* bỏ qua */
      }
    }
    return data;
  } catch (error) {
    sessionStore.set({ loadUser: false });
    throw error;
  }
}

/** Toàn bộ shop, không phân trang — dropdown chọn chủ đơn vị (chỉ ADMIN). */
export async function loadAllUser() {
  sessionStore.set({ isLoadAllUser: true });
  try {
    const { data } = await http.get("/auth/api/v1/admin/get-shops");
    sessionStore.set({ allUser: data, isLoadAllUser: false });
    return data;
  } catch (error) {
    sessionStore.set({ isLoadAllUser: false });
    throw error;
  }
}

/** Shop có phân trang — màn Quản lý tài khoản. */
export async function loadUserShop({ currentPage = 1, pageSize = 10 } = {}) {
  sessionStore.set({ isLoading: true });
  try {
    const { data } = await http.get("/auth/api/v1/admin/get-shops", {
      params: { has_paginate: 1, page: currentPage, size: pageSize },
    });
    sessionStore.set({ userShop: data, isLoading: false });
    return data;
  } catch (error) {
    sessionStore.set({ isLoading: false });
    throw error;
  }
}

/* -------------------------------- Menu store ------------------------------- */

export const menuStore = createStore(
  {
    /** Envelope từ API: `{ data: [...] }` — giữ nguyên shape slice cũ để đỡ sửa call-site. */
    menuOfUser: { data: [] },
    isLoading: false,
    lastFetchTime: null,
  },
  { persistKey: "cms.menu" }
);

export const useMenu = menuStore.use;

/**
 * Nạp menu theo quyền. Cache 5 phút — logic này trước đây bị chia đôi: một nửa ở
 * slice (ghi `menu_cache` vào localStorage), một nửa ở `AppLayout` (so `lastFetchTime`).
 * Giờ nằm gọn một chỗ, nơi gọi chỉ cần `loadMenu()`.
 */
export async function loadMenu({ force = false } = {}) {
  const { menuOfUser, lastFetchTime } = menuStore.get();
  const hasData = menuOfUser?.data?.length > 0;
  if (!force && hasData && lastFetchTime && Date.now() - lastFetchTime < CACHE_TTL_MS) {
    return menuOfUser;
  }

  menuStore.set({ isLoading: true });
  try {
    const { data } = await http.get("/api/v1/security/user/menus");
    menuStore.set({ menuOfUser: data, isLoading: false, lastFetchTime: Date.now() });
    return data;
  } catch (error) {
    menuStore.set({ isLoading: false });
    throw error;
  }
}

/* --------------------------- Đăng nhập / đăng xuất -------------------------- */

/** Lưu token sau khi `/cms/login` trả về. */
export function startSession({ access_token, type_user, permissions }) {
  tokens.set({ accessToken: access_token, typeUser: type_user, permissions });
}

/**
 * Xóa phiên rồi đưa về trang đăng nhập.
 *
 * `tokens.clear()` phát tín hiệu, hai store bên dưới tự reset qua `subscribe` —
 * nên không nơi nào phải nhớ danh sách state cần dọn.
 */
export function endSession({ redirect = true } = {}) {
  tokens.clear();
  cacheClear();
  if (redirect) window.location.assign("/login");
}

/**
 * Token bị xóa (đăng xuất, hoặc 401 ở interceptor) thì dọn state ngay.
 * Đăng ký một lần lúc nạp module, không cần hủy.
 */
tokens.subscribe((authenticated) => {
  if (!authenticated) {
    sessionStore.reset();
    menuStore.reset();
  }
});
